<script setup lang="ts">
import {
	ref,
	computed,
	onMounted,
	onBeforeMount,
	onBeforeUnmount,
	watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useApiClient } from "@/api/client";
import { useBaseStore } from "@/stores/base";
import { useCampaignPlannerStore } from "../../../store";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import FlightMap from "./FlightMap.vue";
import FlightGoalCard from "./FlightGoalCard.vue";
import GeneralInfoSection from "./form/GeneralInfoSection.vue";
import ScreenFiltersSection from "./form/ScreenFiltersSection.vue";
import ScreenSelectionSection from "./form/ScreenSelectionSection.vue";
import { debounce } from "vue-debounce";
import { useAbortController } from "@/modules/explorer/composables/useAbortController";
const { abortController, abortRequestAndResetController } =
	useAbortController();
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import SizeDO = Modules.CampaignPlanner.Data.SizeDO;
import CriterionDO = Modules.CampaignPlanner.Data.CriterionDO;
import ScreenDO = Modules.CampaignPlanner.Data.ScreenDO;
import { toastApiResult } from "@/toaster";

const router = useRouter();

const generalInfoSection = ref<InstanceType<typeof GeneralInfoSection>>();

const isStep1Valid = computed(() => {
	if (!flight.value) return false;
	if (!flight.value.name?.trim()) return false;
	if (flight.value.goal_target == null || Number(flight.value.goal_target) <= 0)
		return false;
	if (flight.value.is_digital) {
		const from = flight.value.from_time_on_days;
		const to = flight.value.to_time_on_days;
		if (from && to && to <= from) return false;
	}
	return true;
});

const isStep3Valid = computed(() => {
	const could =
		(flight.value?.refined_screen_selection_could as number[] | null) ?? [];
	const must =
		(flight.value?.refined_screen_selection_must as number[] | null) ?? [];
	return could.length + must.length > 0;
});

const savingFlight = ref(false);

// Debug-only, temporary: routes this flight's price-quote processing through the new internal
// PQA engine instead of the external legacy one, to compare output. Never persisted.
const useInternalPqaEngine = ref(true);

const saveFlight = async () => {
	if (!flight.value) return;
	savingFlight.value = true;
	const result = await toastApiResult(
		useApiClient("campaignPlanner").saveFlight(
			flight.value,
			useInternalPqaEngine.value
		)
	);
	savingFlight.value = false;
	if (result.isOk()) {
		flight.value = result.value;
		router.push({
			name: "flight.detail.process.segments",
			params: {
				flight_uuid: flight.value.uuid,
			},
		});
	}
};

const isFullscreen = ref(true);

onBeforeMount(() => (useBaseStore().appFullWidth = true));
onBeforeUnmount(() => {
	useBaseStore().appFullWidth = false;
	if (loadingScreensTimer !== null) clearTimeout(loadingScreensTimer);
});

const store = useCampaignPlannerStore();
const flight_uuid: string = (useRoute().params.flight_uuid ?? "").toString();

const flight = ref<FlightDO | null>(null);
const allSizes = ref<SizeDO[]>([]);
const criteria = ref<CriterionDO[]>([]);
const rawAvailableScreens = ref<ScreenDO[]>([]);
const rawExcludedScreens = ref<ScreenDO[]>([]);
const rawLocationPolygons = ref<object[] | null>(null);
const loading = ref(true);
const loadingScreens = ref(false);
const loadingScreensDelayed = ref(false);
let loadingScreensTimer: ReturnType<typeof setTimeout> | null = null;
const activeStep = ref(1);
const scrollRef = ref();

watch(activeStep, () => {
	if (scrollRef.value?.$el) scrollRef.value.$el.scrollTop = 0;
});

watch(loadingScreens, (value) => {
	if (value) {
		loadingScreensTimer = setTimeout(() => {
			loadingScreensDelayed.value = true;
		}, 500);
	} else {
		if (loadingScreensTimer !== null) {
			clearTimeout(loadingScreensTimer);
			loadingScreensTimer = null;
		}
		loadingScreensDelayed.value = false;
	}
});

watch(
	flight,
	() => {
		loadAvailableScreens();
	},
	{ deep: true }
);

watch([() => flight.value?.orientation, () => flight.value?.is_digital], () => {
	if (flight.value) flight.value.selected_sizes = [];
});

watch(
	() => flight.value?.is_digital,
	() => {
		if (flight.value) flight.value.selected_criteria = [];
	}
);

watch(
	() => flight.value?.type_of_buy,
	(newTypeOFBuy) => {
		if (newTypeOFBuy === "goal_impressions") {
			flight.value.goal_name_clean = "reach";
			flight.value.is_vendor_fit_enabled = true;
			flight.value.slot_fraction = 1.0;
			flight.value.segment_granularity = "period";
		}
	}
);

const sizesForFlight = computed(() => {
	if (!flight.value) return allSizes.value;
	const type = flight.value.is_digital ? "digital" : "static";
	return allSizes.value.filter((s) => s.inventory_type?.toLowerCase() === type);
});

const analogSizes = computed(() =>
	allSizes.value.filter((s) => s.inventory_type?.toLowerCase() === "static")
);

const availableScreens = computed(() => {
	if (activeStep.value !== 3) return rawAvailableScreens.value;
	const selectedIds = new Set([
		...((flight.value?.refined_screen_selection_could as number[] | null) ??
			[]),
		...((flight.value?.refined_screen_selection_must as number[] | null) ?? []),
	]);
	return [...rawAvailableScreens.value, ...rawExcludedScreens.value].filter(
		(s) => selectedIds.has(s.id)
	);
});

const excludedScreens = computed(() => {
	if (activeStep.value !== 3) return rawExcludedScreens.value;
	const selectedIds = new Set([
		...((flight.value?.refined_screen_selection_could as number[] | null) ??
			[]),
		...((flight.value?.refined_screen_selection_must as number[] | null) ?? []),
	]);
	return [...rawAvailableScreens.value, ...rawExcludedScreens.value].filter(
		(s) => !selectedIds.has(s.id)
	);
});

const locationPolygons = computed<object[] | null>(
	() =>
		//activeStep.value !== 3 ? rawLocationPolygons.value : [],
		rawLocationPolygons.value
);

const loadAvailableScreens = debounce(async () => {
	if (activeStep.value === 3) return;

	abortRequestAndResetController();

	loadingScreens.value = true;
	const r = await useApiClient("campaignPlanner").getFlightAvailableScreens(
		flight.value,
		abortController.value?.signal ?? null
	);
	if (r.isOk()) {
		rawAvailableScreens.value = r.value.screens;
		rawExcludedScreens.value = r.value.excluded_screens;
		rawLocationPolygons.value = r.value.location_polygons;
	}
	loadingScreens.value = false;
}, 350);

onMounted(async () => {
	const flightRes = await useApiClient("campaignPlanner").getFlight(
		flight_uuid
	);
	if (flightRes.isOk()) {
		flight.value = flightRes.value;

		// reset segments ( only set wehen editing ) but they break because of their size and laravel/data
		flight.value.segments = [];

		const f = flightRes.value;

		const oppName = f.opportunity?.name ?? "Opportunity";

		store.setBreadcrumbs([
			{
				title: "Opportunities",
				disabled: false,
				to: "/campaign-planner/opportunities/listing",
			},
			{
				title: oppName,
				disabled: false,
				to: `/campaign-planner/opportunities/detail/${f.opportunity_uuid}`,
			},
			{ title: f.name ?? "Flight", disabled: true, to: "" },
		]);
	}

	await Promise.all([
		useApiClient("campaignPlanner")
			.getSizes({ page: 1, per_page: 100 })
			.then((r) => {
				if (r.isOk()) allSizes.value = r.value.data;
			}),
		useApiClient("campaignPlanner")
			.getFlightCriteria()
			.then((r) => {
				if (r.isOk()) criteria.value = r.value;
			}),
	]);

	loading.value = false;
});
</script>

<template>
	<Teleport to="body" :disabled="!isFullscreen">
		<div :class="isFullscreen ? 'flight-fullscreen' : 'flight-inapp'">
			<div v-if="loading" class="d-flex align-center justify-center h-100">
				<v-progress-circular indeterminate color="primary" />
			</div>

			<template v-else-if="flight">
				<!-- Toolbar -->
				<v-toolbar v-if="isFullscreen" flat height="60">
					<div class="pl-5">
						<main-breadcrumbs :breadcrumbs="store.breadcrumbs" inline />
					</div>
					<v-spacer />
					<v-tooltip text="Collapse to in-app view" location="bottom">
						<template v-slot:activator="{ props }">
							<v-btn
								icon="fa: fa-regular fa-compress"
								v-bind="props"
								@click="isFullscreen = false"
							/>
						</template>
					</v-tooltip>
				</v-toolbar>
				<v-divider />

				<!-- Content -->
				<div class="flight-layout">
					<div class="flight-form-col">
						<perfect-scrollbar
							ref="scrollRef"
							:options="{ suppressScrollX: true }"
							class="h-100"
						>
							<div class="py-5 px-0">
								<v-stepper-vertical v-model="activeStep" elevation="0" flat>
									<template v-slot:default="{ step }">
										<v-stepper-vertical-item
											:complete="step > 1"
											:color="step > 1 ? '#2563EB' : 'primary'"
											title="General Information"
											value="1"
											style="box-shadow: unset !important"
										>
											<general-info-section
												ref="generalInfoSection"
												:flight="flight"
												:sizes="analogSizes"
											/>

											<template v-slot:next="{ next }">
												<v-btn
													color="primary"
													:disabled="!isStep1Valid"
													@click="
														generalInfoSection
															?.validate()
															.then(({ valid }) => valid && next())
													"
												></v-btn>
											</template>

											<template v-slot:prev></template>
										</v-stepper-vertical-item>

										<v-stepper-vertical-item
											:complete="step > 2"
											:color="step > 2 ? '#2563EB' : 'primary'"
											title="Screen Filters"
											value="2"
											style="box-shadow: unset !important"
										>
											<screen-filters-section
												:flight="flight"
												:sizes="sizesForFlight"
												:criteria="criteria"
												:screens="availableScreens"
												:excluded-screens="excludedScreens"
											/>

											<template v-slot:next="{ next }">
												<v-btn color="primary" @click="next"></v-btn>
											</template>

											<template v-slot:prev="{ prev }">
												<v-btn variant="text" @click="prev"></v-btn>
											</template>
										</v-stepper-vertical-item>

										<v-stepper-vertical-item
											title="Cherry Pick Screens"
											value="3"
											style="box-shadow: unset !important"
										>
											<screen-selection-section
												:flight="flight"
												:available-screens="rawAvailableScreens"
												:excluded-screens="rawExcludedScreens"
												:loading="loadingScreens"
											/>

											<template v-slot:next="">
												<div class="d-flex align-center ga-4">
													<v-switch
														v-model="useInternalPqaEngine"
														label="Use internal PQA engine"
														color="primary"
														density="compact"
														hide-details
														class="flex-grow-0"
													/>
													<v-btn
														color="primary"
														:disabled="!isStep3Valid"
														:loading="savingFlight"
														@click="saveFlight()"
														>Generate Price Quote</v-btn
													>
												</div>
											</template>

											<template v-slot:prev="{ prev }">
												<v-btn variant="text" @click="prev"></v-btn>
											</template>
										</v-stepper-vertical-item>
									</template>
								</v-stepper-vertical>
							</div>
						</perfect-scrollbar>
					</div>

					<div class="flight-map-col">
						<div v-if="loadingScreens" class="map-loading">
							<v-fade-transition>
								<v-progress-linear
									indeterminate
									v-if="loadingScreens"
									height="4"
								/>
							</v-fade-transition>
							<v-fade-transition>
								<v-chip class="mt-3" v-if="loadingScreensDelayed" size="small"
									>Awaiting response from Broadsign API ...</v-chip
								>
							</v-fade-transition>
						</div>
						<flight-map
							:flight="flight"
							:screens="availableScreens"
							:excluded-screens="excludedScreens"
							:location-polygons="locationPolygons"
						/>
						<flight-goal-card :flight="flight" :screens="availableScreens" />
						<v-tooltip
							v-if="!isFullscreen"
							text="Expand to full screen"
							location="bottom"
						>
							<template v-slot:activator="{ props }">
								<v-btn
									icon="fa: fa-regular fa-expand"
									variant="elevated"
									size="small"
									v-bind="props"
									class="map-fullscreen-btn"
									@click="isFullscreen = true"
								/>
							</template>
						</v-tooltip>
					</div>
				</div>
			</template>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
.flight-layout {
	display: flex;
	flex: 1;
	overflow: hidden;
	min-height: 0;
}

.flight-form-col {
	flex: 0 0 50%;
	height: 100%;
	border-right: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.flight-map-col {
	flex: 1;
	height: 100%;
	position: relative;
}

.map-fullscreen-btn {
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 1;
}

.map-loading {
	position: absolute;
	left: 12px;
	right: 12px;
	top: 12px;
	z-index: 1;
}

// Fullscreen mode — teleported to body, covers everything
.flight-fullscreen {
	position: fixed;
	inset: 0;
	z-index: 1999;
	background: rgb(var(--v-theme-background));
	display: flex;
	flex-direction: column;
}

// In-app mode — renders in normal flow inside the layout
.flight-inapp {
	display: flex;
	flex-direction: column;
	height: calc(100dvh - var(--v-layout-top) - 56px);
	overflow: hidden;
}
</style>

<style>
.flight-layout .v-expansion-panel {
	background-color: unset !important;
}
</style>
